import type { Arc, FlowData } from '@/types';
import { CONFIG } from '@/config';
import { store } from '@/state';
import { getThreatColor } from '@/utils/colors';
import { calculateDistance, calculateArcAltitude } from '@/utils/distance';

/** Fade in duration for arcs in milliseconds */
const FADE_IN_DURATION = 200;

/** Fade out duration for arcs in milliseconds */
const FADE_OUT_DURATION = 1800;

/**
 * Add an arc from a flow
 * @param flow - Flow data
 */
export function addArc(flow: FlowData): void {
  const { srcLat, srcLng, dstLat, dstLng, mbps, threat } = flow;
  const state = store.getState();

  // Check if threat level is enabled
  if (!store.isThreatLevelEnabled(Math.floor(threat) as 0 | 1 | 2 | 3 | 4 | 5)) {
    return;
  }

  // Calculate arc properties based on distance and bandwidth
  const distance = calculateDistance(srcLat, srcLng, dstLat, dstLng);
  const altitude = calculateArcAltitude(distance);
  const dashLength = Math.max(0.1, 0.5 - (mbps / 200) * 0.4);
  const dashGap = Math.max(0.05, 0.3 - (mbps / 200) * 0.25);

  const arc: Arc = {
    startLat: srcLat,
    startLng: srcLng,
    endLat: dstLat,
    endLng: dstLng,
    color: getThreatColor(threat, 0), // Start transparent
    dashLength,
    dashGap,
    altitude,
    createdAt: Date.now(),
    mbps,
    threat,
  };

  const arcs = state.arcs;

  if (arcs.length < CONFIG.maxArcs) {
    arcs.push(arc);
  } else {
    const index = store.nextArcIndex();
    arcs[index] = arc;
  }

  store.set('arcsDirty', true);

  // Track high-threat flows
  if (threat >= 4) {
    store.incrementThreats();
  }
}

/**
 * Update all arcs (fade in/out, remove expired)
 */
export function updateArcs(): void {
  const state = store.getState();
  const now = Date.now();
  let writeIndex = 0;

  const arcs = state.arcs;
  let dirty = state.arcsDirty || arcs.length > 0;

  for (let i = 0; i < arcs.length; i++) {
    const arc = arcs[i];
    const age = now - arc.createdAt;

    // Remove expired arcs
    if (age > CONFIG.arcLifetime) {
      continue;
    }

    // Calculate opacity based on age
    let opacity: number;
    if (age < FADE_IN_DURATION) {
      // Fade in
      opacity = age / FADE_IN_DURATION;
    } else {
      // Fade out
      const fadeAge = age - FADE_IN_DURATION;
      const fadeProgress = fadeAge / FADE_OUT_DURATION;
      opacity = 1 - fadeProgress;
    }

    // Update arc color with new opacity
    arc.color = getThreatColor(arc.threat, opacity);
    arcs[writeIndex] = arc;
    writeIndex++;
  }

  // Compact the array if needed
  if (writeIndex !== arcs.length) {
    arcs.length = writeIndex;
    dirty = true;
  }

  // Update globe if dirty
  if (dirty) {
    const globe = state.globe;
    if (globe) {
      globe.arcsData(arcs);
    }
    store.set('arcsDirty', false);
  }
}
